import { api } from './index'

export interface BrameCampaign {
  id: string
  name: string
  campaign_url: string
  merchant_id: string
  created_at: string
  updated_at: string
}

type CreateBrameCampaignRequest = {
  name: string
  campaign_url: string
}

type UpdateBrameCampaignRequest = {
  id: string
  name: string
  campaign_url: string
}

export const brameCampaignsApi = api.injectEndpoints({
  endpoints: builder => ({
    // Get all brame campaigns
    getBrameCampaigns: builder.query<BrameCampaign[], void>({
      query: () => ({
        url: 'brame_campaigns',
        method: 'GET',
      }),
      transformResponse: (response: { brame_campaigns: BrameCampaign[] }) => {
        return response.brame_campaigns || []
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'BrameCampaign' as const,
                id,
              })),
              { type: 'BrameCampaign', id: 'LIST' },
            ]
          : [{ type: 'BrameCampaign', id: 'LIST' }],
    }),

    // Get single brame campaign by ID
    getBrameCampaign: builder.query<BrameCampaign, string>({
      query: id => ({
        url: `brame_campaigns/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { brame_campaign: BrameCampaign }) =>
        response.brame_campaign,
      providesTags: (result, error, id) => [{ type: 'BrameCampaign', id }],
    }),

    // Create new brame campaign
    createBrameCampaign: builder.mutation<
      BrameCampaign,
      CreateBrameCampaignRequest
    >({
      query: campaignData => ({
        url: 'brame_campaigns',
        method: 'POST',
        body: campaignData,
      }),
      transformResponse: (response: { brame_campaign: BrameCampaign }) =>
        response.brame_campaign,
      invalidatesTags: [{ type: 'BrameCampaign', id: 'LIST' }],
    }),

    // Update existing brame campaign
    updateBrameCampaign: builder.mutation<
      BrameCampaign,
      UpdateBrameCampaignRequest
    >({
      query: ({ id, ...campaignData }) => ({
        url: `brame_campaigns/${id}`,
        method: 'PATCH',
        body: campaignData,
      }),
      transformResponse: (response: { brame_campaign: BrameCampaign }) =>
        response.brame_campaign,
      invalidatesTags: (result, error, { id }) => [
        { type: 'BrameCampaign', id },
        { type: 'BrameCampaign', id: 'LIST' },
      ],
    }),

    // Delete brame campaign
    deleteBrameCampaign: builder.mutation<void, string>({
      query: id => ({
        url: `brame_campaigns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'BrameCampaign', id },
        { type: 'BrameCampaign', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useGetBrameCampaignsQuery,
  useGetBrameCampaignQuery,
  useCreateBrameCampaignMutation,
  useUpdateBrameCampaignMutation,
  useDeleteBrameCampaignMutation,
} = brameCampaignsApi
